import React from "react";
import { useLocalStorage } from "react-use";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as Driver from "~/Driver";
import { HoursOfService as GenericHoursOfService } from "~/HoursOfService";
import * as Preferences from "~/Preferences";
import { Remote } from "~/Remote";
import { User } from "~/User";

export type Filter = { readonly filter: Filter.Value & Filter.Update };
export function Filter({ filter, children, ...controls }: Filter.Props) {
  return (
    <Filter.Container>
      <Filter.Controls {...controls} />
      {children}
    </Filter.Container>
  );
}

export namespace Filter {
  export type Value = Partial<
    Driver.Drivers &
      Driver.Table.DeviceType.Filter & {
        readonly hoursOfService: Partial<
          GenericHoursOfService.Types & GenericHoursOfService.Exemptions
        >;
      }
  >;

  export type Props = Filter &
    Filter.Controls.Props & { readonly children?: React.ReactNode };

  export type Update = { readonly update: (filter: Filter) => void };
  export namespace Update {
    export const drivers = (drivers?: Partial<Driver.Drivers>) => ({
      filter,
    }: Filter): Filter => ({
      filter: { ...filter, ...(drivers ?? { drivers: undefined }) },
    });

    export namespace HoursOfService {
      export const types = (types?: Partial<GenericHoursOfService.Types>) => ({
        filter,
      }: Filter): Filter => ({
        filter: {
          ...filter,
          hoursOfService: {
            ...filter.hoursOfService,
            ...(types ?? { types: undefined }),
          },
        },
      });

      export const exemptions = (
        exemptions?: Partial<GenericHoursOfService.Exemptions>
      ) => ({ filter }: Filter): Filter => ({
        filter: {
          ...filter,
          hoursOfService: {
            ...filter.hoursOfService,
            ...(exemptions ?? { exemptions: undefined }),
          },
        },
      });
    }

    export namespace DeviceType {
      export const isOutdated = (isOutdated?: boolean) => ({
        filter,
      }: Filter): Filter => ({
        filter: {
          ...filter,
          device: {
            ...filter.device,
            isOutdated,
          },
        },
      });

      export const isMissingApplication = (isMissingApplication?: boolean) => ({
        filter,
      }: Filter): Filter => ({
        filter: {
          ...filter,
          device: {
            ...filter.device,
            isMissingApplication,
          },
        },
      });
    }
  }

  export const isActive = ({ filter }: Filter) =>
    [
      ...(filter?.drivers ?? []),
      ...(filter?.hoursOfService?.types ?? []),
      ...(filter?.hoursOfService?.exemptions ?? []),
      ...(filter?.device?.isOutdated ? [true] : []),
      ...(filter?.device?.isMissingApplication ? [true] : []),
    ].length > 0;

  export const applies = ({ filter }: Filter) => (
    driver: Driver.Driver
  ): boolean =>
    !Filter.isActive({ filter }) ||
    !!filter?.drivers?.find(({ id }) => id === driver.id) ||
    !!filter?.hoursOfService?.types?.find(
      ({ type }) => type.id === driver.hoursOfService?.type?.id
    ) ||
    !!filter?.hoursOfService?.exemptions?.find(
      ({ exemption }) => exemption.id === driver.hoursOfService?.exemption?.id
    ) ||
    !!Driver.Table.DeviceType.Filter.applies({ filter })(driver);

  export const style = (): React.CSSProperties => ({ width: 400 });

  export const use = (): Filter => {
    const user = User.use();

    const [value, setValue] = useLocalStorage<Filter.Value>(
      `driver-table-filter-${user.id}`,
      undefined
    );

    return { filter: { ...value, update: ({ filter }) => setValue(filter) } };
  };

  export const Container = styled.div`
    & > div {
      border-bottom: 1px solid #f0f0f0;
      &:last-of-type {
        border-bottom: none;
      }
    }
  `;

  export const Section = styled.div`
    padding: 0 4px 4px 4px;
  `;

  export function Controls({ clear, confirm }: Controls.Props) {
    return (
      <Controls.Container>
        <Button type="link" size="small" disabled={!clear} onClick={clear}>
          Reset
        </Button>
        <Button type="primary" size="small" onClick={confirm}>
          OK
        </Button>
      </Controls.Container>
    );
  }

  export namespace Controls {
    export type Props = {
      readonly clear?: () => void;
      readonly confirm: () => void;
    };

    export const Container = styled.div`
      display: flex;
      justify-content: space-between;
      padding: 8px;
      margin-bottom: 4px;
    `;
  }

  export namespace Location {
    export const useSelectedWorkReportingLocations = () => {
      const [loading, locationIds] = Remote.when(
        Preferences.Get.use<Driver.Filter.Filter>(),
        {
          loading: () => [true, []],
          error: () => [false, []],
          success: (preferences) => {
            return [
              false,
              preferences[Preferences.preferenceKey("locationFilter")] ?? [],
            ];
          },
        }
      );

      return loading ? undefined : locationIds;
    };
  }
}
